import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useFormFieldVisibility } from "@/context/FormFieldVisibilityContext";
import { groupFieldsByBaseLocale } from "@/lib/form-field-visibility";
import { tWithKey } from "@/lib/i18n-fallback";
import { Eye } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Button + drawer that lets the user pick which fields of a single tab are
 * shown (expanded) or masked. Fields self-register at mount, so the list
 * always reflects the tab currently rendered.
 *
 * `standalone`: rendered on its own right-aligned row, for tabs without a
 * header (otherwise a field masked there could never be shown again).
 */
export function FormTabFieldsDrawer({
  tabValue,
  tabLabel,
  standalone = false,
}: {
  tabValue: string;
  tabLabel?: string;
  standalone?: boolean;
}) {
  const { t } = useTranslation();
  const {
    enabled,
    getRegisteredFields,
    registryVersion,
    isExpanded,
    toggleVisibility,
  } = useFormFieldVisibility();

  // One entry per field, multilingual variants (x.fr, x.en…) merged into one.
  const groups = useMemo(() => {
    const all = getRegisteredFields();
    // Sheet-level fields (rendered in the header, outside any tab, e.g. the
    // artwork status) have no tab → show them at the top of every tab drawer.
    const sheetLevel = all.filter((f) => f.tab == null);
    const ownFields = all.filter((f) => f.tab === tabValue);
    return groupFieldsByBaseLocale([...sheetLevel, ...ownFields]);
    // registryVersion changes whenever a field mounts/unmounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRegisteredFields, registryVersion, tabValue]);

  if (!enabled || groups.length === 0) {
    return null;
  }

  const isGroupShown = (names: string[]) => names.every(isExpanded);
  const setGroupShown = (names: string[], shown: boolean) =>
    names.forEach((n) => {
      if (isExpanded(n) !== shown) toggleVisibility(n);
    });

  const maskableGroups = groups.filter((g) => !g.nonMaskable);

  const drawer = (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2 bg-blue-50 hover:bg-blue-100"
          title={tWithKey(
            t,
            "fieldVisibility.tab.manageFields",
            "Gérer les champs affichés",
          )}
        >
          <Eye className="h-4 w-4" />
          {tWithKey(t, "fieldVisibility.tab.fields", "Champs")}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex flex-col gap-0 overflow-y-auto p-6 pt-10">
        <SheetHeader>
          <SheetTitle>
            {tWithKey(
              t,
              "fieldVisibility.tab.manageFields",
              "Gérer les champs affichés",
            )}
            {tabLabel ? ` — ${tabLabel}` : ""}
          </SheetTitle>
          <SheetDescription>
            {tWithKey(
              t,
              "fieldVisibility.tab.description",
              "Choisissez les champs à afficher dans cet onglet.",
            )}
          </SheetDescription>
        </SheetHeader>

        {maskableGroups.length > 0 && (
          <div className="flex items-center justify-end gap-2 py-3">
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() =>
                maskableGroups.forEach((g) => setGroupShown(g.names, true))
              }
            >
              {tWithKey(t, "fieldVisibility.showAll", "Tout afficher")}
            </Button>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() =>
                maskableGroups.forEach((g) => setGroupShown(g.names, false))
              }
            >
              {tWithKey(t, "fieldVisibility.hideAll", "Tout masquer")}
            </Button>
          </div>
        )}

        <ul className="flex flex-col divide-y">
          {groups.map((group) => {
            const shown = isGroupShown(group.names);
            const checked = group.nonMaskable || shown;
            const displayLabel = group.label ?? t(group.baseKey);
            return (
              <li
                key={group.baseKey}
                className="flex items-center justify-between gap-4 py-2.5"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                  {displayLabel}
                </span>
                <Switch
                  checked={checked}
                  disabled={group.nonMaskable}
                  onCheckedChange={() =>
                    !group.nonMaskable && setGroupShown(group.names, !shown)
                  }
                />
              </li>
            );
          })}
        </ul>
      </SheetContent>
    </Sheet>
  );

  return standalone ? (
    <div className="flex w-full justify-end">{drawer}</div>
  ) : (
    drawer
  );
}
